import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { RootState } from '@AppState/redux-store'
import { AuthScopes, ResendInvitationRequest } from '@Generated/back-office-client/src'
import { ExclamationTriangleIcon, MagnifyingGlassIcon } from '@radix-ui/react-icons'
import {
  Button,
  DropdownMenu,
  Flex,
  IconButton,
  Skeleton,
  Table,
  Text,
  TextField,
  Link as ThemedLink,
} from '@radix-ui/themes'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { DeleteUserAlert } from './delete-user-alert'
import { UsersServiceAPI } from '@API/data-services/users-service'
import { Pagination } from '@Components/common/pagination'
import { usePaginatedState } from '@Components/common/use-paginated-state'
import { Routes } from '@Components/main-app/types'
import { LoadingRows } from '@Components/util/loading-rows'

const PAGE_SIZE = 25
const SEARCH_DEBOUNCE_MS = 300
const MIN_SEARCH_LENGTH = 3

const PendingInviteMenu = ({
  onResendInvite,
  onDeleteInvite,
  onCopyToClipboard,
  sending,
}: {
  onResendInvite: () => void
  onDeleteInvite: () => void
  onCopyToClipboard: () => void
  sending: boolean
}) => {
  const [open, setOpen] = useState(false)

  return (
    <DropdownMenu.Root open={open} onOpenChange={setOpen}>
      <DropdownMenu.Trigger>
        <IconButton variant="ghost" loading={sending} style={{ width: '100%', cursor: 'pointer' }}>
          <ExclamationTriangleIcon />
        </IconButton>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>
        <DropdownMenu.Item
          onClick={() => {
            setOpen(false)
            onCopyToClipboard()
          }}>
          Copy Invitation Link
        </DropdownMenu.Item>
        <DropdownMenu.Item onClick={onResendInvite}>Resend Invitation</DropdownMenu.Item>
        <DropdownMenu.Item onClick={onDeleteInvite}>Delete Invitation</DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export const UsersList = () => {
  const [reinvitedUser, setReinvitedUser] = useState<string | null>(null)
  const { currentUser, activeCustomerId } = useSelector((state: RootState) => state.users)

  const {
    mutate: resendInvitation,
    isPending,
    isSuccess: resendInvitationSuccess,
  } = useMutation({
    mutationFn: (resendInvitationRequest: ResendInvitationRequest) => {
      return UsersServiceAPI.resendInvitation(resendInvitationRequest)
    },
  })

  const [page, setPage] = usePaginatedState()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [search])

  // Short terms can't use the pg_trgm index, so they don't trigger a search.
  const effectiveSearch =
    debouncedSearch.trim().length >= MIN_SEARCH_LENGTH ? debouncedSearch.trim() : ''

  const {
    data: usersPage,
    refetch: refetchUsers,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [`${activeCustomerId}-users`, effectiveSearch, page],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: () =>
      UsersServiceAPI.getAllUsersInOrg(
        page * PAGE_SIZE,
        page * PAGE_SIZE + PAGE_SIZE,
        effectiveSearch === '' ? undefined : effectiveSearch,
      ),
  })

  const onSearchChange = (next: string) => {
    setSearch(next)
    setPage(0)
  }

  const { mutate: deleteUser } = useMutation({
    mutationFn: (authProviderId: string) => {
      return UsersServiceAPI.deleteUser(authProviderId)
    },
    onSuccess: () => {
      refetchUsers()
    },
  })

  const { mutate: deleteUserInvitation } = useMutation({
    mutationFn: (authProviderId: string) => {
      return UsersServiceAPI.deleteUserInvitation(authProviderId)
    },
    onSuccess: () => {
      refetchUsers()
    },
  })

  const canWrite = currentUser?.scopes.includes(AuthScopes.writeusers)

  const rows = usersPage?.results.map((u) => {
    const resendingToUser = isPending && reinvitedUser === u.authProviderId

    const pendingInvitation = u.invitation ? (
      <PendingInviteMenu
        sending={resendingToUser}
        onCopyToClipboard={() => {
          navigator.clipboard.writeText(u.invitation!)
        }}
        onResendInvite={() => {
          resendInvitation({ userId: u.authProviderId })
          setReinvitedUser(u.authProviderId)
        }}
        onDeleteInvite={() => {
          deleteUserInvitation(u.authProviderId)
        }}
      />
    ) : null

    const reinviteSent = resendInvitationSuccess && reinvitedUser === u.authProviderId

    const inviteCell = reinviteSent ? <Text>Sent</Text> : u.invitation ? pendingInvitation : null

    const editLink = canWrite ? (
      <ThemedLink asChild>
        <Link to={`${Routes.EDIT_USER.replace(':authProviderId', u.authProviderId)}`} state={u}>
          {u.authProviderId}
        </Link>
      </ThemedLink>
    ) : (
      u.authProviderId
    )

    return (
      <Table.Row key={u.authProviderId}>
        <Table.RowHeaderCell>{editLink}</Table.RowHeaderCell>
        <Table.Cell>{u.name}</Table.Cell>
        <Table.Cell>{u.email}</Table.Cell>
        <Table.Cell>
          {u.roles
            ? u.roles
                .filter((r) => !!r)
                .map((r) => r.name)
                .join(',')
            : ''}
        </Table.Cell>
        <Table.Cell>
          <Flex justify="center">{inviteCell}</Flex>
        </Table.Cell>
        <Table.Cell>
          {canWrite && !pendingInvitation ? (
            <Flex justify="center">
              <DeleteUserAlert userName={u.name} onDelete={() => deleteUser(u.authProviderId)} />
            </Flex>
          ) : (
            ''
          )}
        </Table.Cell>
      </Table.Row>
    )
  })

  return (
    <Flex justify="center" direction="column" gap="5">
      <Flex justify="between" align="end" gap="3">
        <TextField.Root
          placeholder="Search by name or email"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          style={{ flex: 1, maxWidth: 360 }}>
          <TextField.Slot>
            <MagnifyingGlassIcon />
          </TextField.Slot>
        </TextField.Root>
        {canWrite ? (
          <Link to={Routes.CREATE_USER}>
            <Button style={{ cursor: 'pointer' }}>Create</Button>
          </Link>
        ) : null}
      </Flex>

      {search.trim().length > 0 && search.trim().length < MIN_SEARCH_LENGTH ? (
        <Text size="1" color="gray">
          Type at least {MIN_SEARCH_LENGTH} characters to search.
        </Text>
      ) : null}

      {isError ? <Flex justify="center">Error loading users</Flex> : null}

      {isError ? null : (
        <Skeleton loading={isLoading}>
          <Table.Root>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>User Id</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Email</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Roles</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Invitation Pending</Table.ColumnHeaderCell>
                {canWrite ? <Table.ColumnHeaderCell></Table.ColumnHeaderCell> : null}
              </Table.Row>
            </Table.Header>

            <Table.Body>{isLoading ? <LoadingRows /> : rows}</Table.Body>
          </Table.Root>
        </Skeleton>
      )}

      {!isLoading && usersPage ? (
        <Pagination
          totalItems={usersPage.totalResults}
          pageSize={PAGE_SIZE}
          currentPage={page}
          onPageChange={setPage}
        />
      ) : null}
    </Flex>
  )
}
